<template>
  <div ref="chartContainer" class="voyage-compare-chart">
    <chart-toolbar
      :locked="!!lockedY"
      :zoomed="!!zoomRange"
      :lockable="!!tkCurrentDomains"
      :exportable="!loading && !!tkCurrentDomains"
      @lock="tkToggleLockY(tkCurrentDomains)"
      @reset="tkPublishZoom(null)"
      @png="tkExportPng(fileName)"
      @csv="exportCsv"
    />
    <div v-if="loading" class="voyage-compare-chart-skeleton" :style="{ height: `${height}px` }" aria-busy="true">
      <span class="sr-only">Loading...</span>
    </div>
    <div v-else-if="!hasData" class="voyage-compare-chart-empty">No data available</div>
    <div v-show="!loading && hasData" ref="canvas" class="voyage-compare-chart-canvas"></div>
    <div ref="tooltip" class="voyage-compare-chart-tooltip"></div>
    <div class="voyage-compare-chart-legend">
      <button
        v-for="ds in datasets"
        :key="ds.id"
        type="button"
        class="voyage-compare-chart-legend-item"
        :class="{ 'is-hidden': hiddenSeries[ds.name] }"
        @click="tkToggleSeries(ds.name)"
      >
        <span class="voyage-compare-chart-swatch" :style="swatchStyle(ds)"></span>
        <span>{{ ds.name }}</span>
        <span v-if="ds.status === 'empty'" class="voyage-compare-chart-status">(no data)</span>
      </button>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatNum } from '@/util/number';
import { formatDateTime } from '@/util/date';
import { CHART_TOOLTIP } from '@/util/apiDatetime';
import { escapeHtml } from '@/util/trail/voyageMapExtras';
import chartToolkit from '@/components/charts/chartToolkit';
import ChartToolbar from '@/components/charts/ChartToolbar.vue';
import { formatElapsed, elapsedTicks } from '@/views/voyage/utils/voyageCompareSeries';

export default {
  name: 'VoyageCompareChart',
  mixins: [chartToolkit],
  components: { ChartToolbar },
  props: {
    chartId: { type: String, required: true },
    // ms sejak voyage mulai
    timestamps: { type: Array, default: () => [] },
    datasets: { type: Array, default: () => [] },
    yLabel: { type: String, default: '' },
    unit: { type: String, default: '' },
    decimals: { type: Number, default: 1 },
    loading: { type: Boolean, default: false },
    syncGroup: { type: String, default: 'voyage-compare' },
    fileName: { type: String, default: 'voyage-compare' },
    // detik unix mulai tiap sisi, buat jam asli di tooltip
    startSeconds: { type: Object, default: () => ({}) },
    height: { type: Number, default: 300 },
  },
  data() {
    return {
      resizeObserver: null,
      tkCurrentDomains: null,
    };
  },
  computed: {
    hasData() {
      return this.timestamps.length > 0 && this.datasets.some((ds) => ds.status === 'ok');
    },
  },
  watch: {
    datasets() {
      this.$nextTick(this.drawChart);
    },
    loading() {
      this.$nextTick(this.drawChart);
    },
  },
  created() {
    this.tkLast = null;
    this.lastWidth = 0;
    this.tkKeyExtra = this.chartId;
  },
  mounted() {
    this.tkBind();
    this.drawChart();
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.$el.clientWidth;
      if (width === this.lastWidth) return;
      this.lastWidth = width;
      this.drawChart();
    });
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    this.tkUnbind();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  methods: {
    swatchStyle(ds) {
      return {
        borderTopColor: ds.color,
        borderTopStyle: ds.dash ? 'dashed' : 'solid',
      };
    },
    // sumbu X bukan tanggal, nama file gak ikut rentang
    tkFileName(base) {
      return base;
    },
    exportCsv() {
      if (!this.tkLast) return;
      this.tkExportCsv(this.fileName, this.tkLast.timestamps, this.tkLast.datasets, formatElapsed);
    },
    drawChart() {
      const container = this.$refs.canvas;
      if (!container) return;
      d3.select(container).selectAll('*').remove();
      this.tkCurrentDomains = null;
      this.tkLast = null;
      if (this.loading || !this.hasData) return;

      const outerWidth = Math.max(container.clientWidth, 320);
      const compact = outerWidth < 576;
      const margin = {
        top: 12,
        right: 16,
        bottom: 32,
        left: compact ? 48 : 60,
      };
      const width = Math.max(1, outerWidth - margin.left - margin.right);
      const height = this.height - margin.top - margin.bottom;
      const tickCount = compact ? 4 : 6;
      const { timestamps } = this;

      const svg = d3.select(container).append('svg')
        .attr('width', outerWidth)
        .attr('height', this.height)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const clipId = `vcc-clip-${this._uid}`;
      svg.append('defs').append('clipPath').attr('id', clipId)
        .append('rect')
        .attr('width', width)
        .attr('height', height);
      const dataLayer = svg.append('g').attr('clip-path', `url(#${clipId})`);

      const all = this.datasets.map((ds) => ({ ...ds, axis: 'left', interpolated: ds.data }));
      const visible = this.tkVisible(all).filter((ds) => ds.status === 'ok');

      const x = d3.scaleLinear().domain(this.tkXDomain(timestamps).map(Number)).range([0, width]);
      const [xFrom, xTo] = x.domain();
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickValues(elapsedTicks(xFrom, xTo, compact ? 4 : 8)).tickFormat(formatElapsed));

      const yDomain = this.tkDomain('left', visible, 10);
      this.tkCurrentDomains = { left: yDomain };
      const y = d3.scaleLinear()
        .domain([yDomain[0], yDomain[1] + (yDomain[1] - yDomain[0]) * 0.05])
        .range([height, 0]);
      svg.append('g').call(d3.axisLeft(y).ticks(tickCount));

      if (this.yLabel) {
        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('y', compact ? -36 : -45)
          .attr('x', -height / 2)
          .attr('text-anchor', 'middle')
          .attr('fill', '#6c757d')
          .attr('font-size', '12px')
          .text(this.yLabel);
      }

      svg.append('g')
        .attr('class', 'grid')
        .attr('opacity', 0.1)
        .call(d3.axisLeft(y).ticks(tickCount).tickSize(-width).tickFormat(''));

      visible.forEach((ds) => {
        dataLayer.append('path')
          .datum(ds.data)
          .attr('fill', 'none')
          .attr('stroke', ds.color)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', ds.dash)
          .attr('d', d3.line()
            .defined((value) => value !== null && value !== undefined)
            .x((value, i) => x(timestamps[i]))
            .y((value) => y(value)));
      });

      // export ikut zoom & seri yang keliatan
      const shown = [];
      timestamps.forEach((ms, i) => { if (ms >= xFrom && ms <= xTo) shown.push(i); });
      this.tkLast = {
        timestamps: shown.map((i) => timestamps[i]),
        datasets: visible.map((ds) => ({ name: `${ds.name} (${this.unit})`, data: shown.map((i) => ds.data[i]) })),
      };

      const focus = svg.append('g').style('display', 'none');
      const focusLine = focus.append('line')
        .attr('stroke', '#aaa')
        .attr('stroke-dasharray', '3 3')
        .attr('y1', 0)
        .attr('y2', height);
      const focusDots = visible.map((ds) => focus.append('circle')
        .attr('r', 5)
        .attr('fill', ds.color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2));

      this.tkOnHover((ms) => {
        if (ms === null || ms < xFrom || ms > xTo) {
          focus.style('display', 'none');
          return;
        }
        focus.style('display', null);
        focusLine.attr('x1', x(ms)).attr('x2', x(ms));
      });

      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((value) => value).left;
      const hoverBase = this.tkInstallBrush(svg, x, width, height);
      hoverBase
        .on('mouseover', () => {
          tooltip.style('opacity', 1);
          focus.style('display', null);
        })
        .on('mouseout', () => {
          tooltip.style('opacity', 0);
          focus.style('display', 'none');
          this.tkPublishHover(null);
        })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          let index = 0;
          if (i > 0 && i < timestamps.length) index = x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1;
          else if (i >= timestamps.length) index = timestamps.length - 1;
          const ms = timestamps[index];
          const xPos = x(ms);
          focusLine.attr('x1', xPos).attr('x2', xPos);
          this.tkPublishHover(ms);

          let html = `<strong>+${escapeHtml(formatElapsed(ms))}</strong>`;
          visible.forEach((ds, idx) => {
            const value = ds.data[index];
            if (value === null || value === undefined) {
              focusDots[idx].style('display', 'none');
              return;
            }
            focusDots[idx].attr('cx', xPos).attr('cy', y(value)).style('display', null);
            const start = this.startSeconds[ds.side];
            const real = Number.isFinite(start)
              ? ` <span style="opacity:.7">(${formatDateTime(start * 1000 + ms, CHART_TOOLTIP)})</span>`
              : '';
            html += `<div style="display:flex;align-items:center;gap:5px;"><span style="width:8px;height:8px;border-radius:50%;background:${escapeHtml(ds.color)};"></span>${escapeHtml(ds.name)}: ${formatNum(value, this.decimals)} ${escapeHtml(this.unit)}${real}</div>`;
          });

          const [posX, posY] = d3.pointer(event, this.$refs.chartContainer);
          let left = posX + 15;
          if (left + 280 > outerWidth) left = posX - 290;
          tooltip.html(html).style('left', `${Math.max(0, left)}px`).style('top', `${posY + 15}px`);
        });
    },
  },
};
</script>

<style scoped>
.voyage-compare-chart {
  position: relative;
}

.voyage-compare-chart ::v-deep [font-family] {
  font-family: inherit;
}

.voyage-compare-chart-canvas {
  width: 100%;
}

.voyage-compare-chart-skeleton {
  width: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%);
  background-size: 400% 100%;
  animation: voyage-compare-chart-shimmer 1.4s ease infinite;
}

@keyframes voyage-compare-chart-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.voyage-compare-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  font-size: 14px;
  color: #6c757d;
}

.voyage-compare-chart-tooltip {
  position: absolute;
  z-index: 10;
  max-width: 280px;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  font-size: 12px;
  pointer-events: none;
  opacity: 0;
}

.voyage-compare-chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  padding-top: 4px;
}

.voyage-compare-chart-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  border: 0;
  background: transparent;
  font-size: 12.25px;
  color: #374151;
  cursor: pointer;
}

.voyage-compare-chart-legend-item.is-hidden {
  opacity: 0.4;
  text-decoration: line-through;
}

.voyage-compare-chart-swatch {
  width: 18px;
  height: 0;
  border-top-width: 3px;
}

.voyage-compare-chart-status {
  color: #b45309;
}
</style>
